import React from "react";
import { motion } from "framer-motion";
import {
  Scissors,
  Sparkles,
  Flower2,
  TreeDeciduous,
  Leaf,
  CalendarCheck,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "@/lib/site-data";

const ICONS = {
  Scissors,
  Sparkles,
  Flower2,
  TreeDeciduous,
  Leaf,
  CalendarCheck,
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.06, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="md:col-span-7"
          >
            <div className="text-xs tracking-[0.28em] uppercase font-bold text-[#2E5C3E] mb-4">
              What we do
            </div>
            <h2 className="font-['Outfit'] text-4xl md:text-6xl font-black tracking-tighter text-[#1A2E20] leading-[1.02]">
              Six things we do <em className="not-italic text-[#2E5C3E]">really</em> well —
              all under one crew.
            </h2>
          </motion.div>
          <motion.p
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={1}
            className="md:col-span-5 text-base md:text-lg text-[#4A6E55] leading-relaxed"
          >
            No subcontractors, no surprises. We mow it, shape it, plant it, and
            haul it away — then come back next week and do it again.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Leaf;
            return (
              <motion.article
                key={s.title}
                data-testid={`service-card-${i}`}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                custom={i}
                className={`${s.span} relative rounded-3xl p-8 md:p-10 lift-on-hover overflow-hidden border ${
                  s.accent
                    ? "bg-[#2E5C3E] border-[#1B3624] text-white"
                    : "bg-white border-[#D1DED5] text-[#1A2E20]"
                }`}
              >
                <div
                  className={`flex items-center justify-center w-12 h-12 rounded-2xl mb-6 ${
                    s.accent
                      ? "bg-[#EAB308] text-[#422006]"
                      : "bg-[#E5EDE7] text-[#2E5C3E]"
                  }`}
                >
                  <Icon className="w-6 h-6" strokeWidth={2.2} />
                </div>
                <h3
                  className={`font-['Outfit'] text-2xl md:text-3xl font-bold tracking-tight mb-3 ${
                    s.accent ? "text-white" : "text-[#1A2E20]"
                  }`}
                >
                  {s.title}
                </h3>
                <p
                  className={`text-base leading-relaxed max-w-md ${
                    s.accent ? "text-white/85" : "text-[#4A6E55]"
                  }`}
                >
                  {s.blurb}
                </p>
                <div
                  className={`mt-7 inline-flex items-center gap-1.5 text-sm font-bold tracking-wide ${
                    s.accent ? "text-[#EAB308]" : "text-[#2E5C3E]"
                  }`}
                >
                  <span className="link-draw">Learn more</span>
                  <ArrowUpRight className="w-4 h-4" />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
